import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {l} from "types/typescriptdefinisjoner_lederplan";
import _ from "lodash";
import {IModule} from "angular";
import {gettextCatalog} from "angular-gettext";
import {noBankidLogo, seBankidLogo} from "../../komponenter/autentisering/criiptoautentiseringBasis";
import {SignaturmetodeModell, SigneringsmetodeModell} from "../ressurser/DokumentsigneringRessurs";
import moment from "moment";
import {Konfigurasjon} from "../../infrastruktur/Konfigurasjon";

//ng2 ekvivalent: src/scripts/felles-ng2/tjenester/DokumentSigneringInformasjon.ts
export class DokumentsigneringTjeneste {

    static regNavn = "DokumentsigneringTjeneste";

    static registrerTjenester(angular: IModule) {
        angular
          .service(DokumentsigneringTjeneste.regNavn, DokumentsigneringTjeneste.forInjisering());
    }

    static forInjisering() {
        return [Konfigurasjon.regNavn, regNavn.gettext.gettextCatalog, DokumentsigneringTjeneste]
    }

    constructor(private konfigurasjon: Konfigurasjon, private gettextCatalog: gettextCatalog) {
    }

    signaturinformasjonSomTekst(signaturinformasjon: l.SignaturinformasjonModell, linjeskift = ' ') {
        const signaturtype = this.signaturtypeI18nNavn(signaturinformasjon.signaturtype);
        const signertDato = moment(signaturinformasjon.signertDato)
        return this.gettextCatalog.getString('Signert av {{identitet}}{{linjeskift}}{{dato}} {{tidssone}}{{linjeskift}}med {{signaturtype}}', {
            linjeskift: linjeskift,
            signaturtype: signaturtype,
            identitet: signaturinformasjon.signertAv,
            dato: signertDato.format('lll'),
            tidssone: signertDato.format('z')
        });
    }

    /** brukerorientert navn på signaturtype. NB må holdes i synk med tilsvarende i BenyttetSigneringsmetode.java */
    signaturtypeI18nNavn(signaturtype: l.BenyttetSigneringsmetode) {
        switch (signaturtype) {
            case l.BenyttetSigneringsmetode.legacy_autentisert:
            case l.BenyttetSigneringsmetode.sp2_enkel:
                return this.gettextCatalog.getString('Enkel signatur som innlogget bruker');
            case l.BenyttetSigneringsmetode.lederplan_autentisert_tofakt_sms:
                return this.gettextCatalog.getString('Engangskode per SMS');
           case l.BenyttetSigneringsmetode.lederplan_autentisert_tofakt_epost :
                return this.gettextCatalog.getString('Engangskode per e-post');
            case l.BenyttetSigneringsmetode.criipto_no_bankid_mobil_tekst:
                return this.gettextCatalog.getString('Norsk BankID');
            case l.BenyttetSigneringsmetode.criipto_no_bankid_central_tekst:
            case l.BenyttetSigneringsmetode.criipto_no_bankid_central_pdf:
            case l.BenyttetSigneringsmetode.criipto_no_bankid_pdf:
            case l.BenyttetSigneringsmetode.criipto_no_bankid_high_pdf:
            case l.BenyttetSigneringsmetode.legacy_no_bankid_central_pdf:
            case l.BenyttetSigneringsmetode.sp2_bankid:
            case l.BenyttetSigneringsmetode.bankid_qes:
                return this.gettextCatalog.getString('Norsk BankID');
            case l.BenyttetSigneringsmetode.criipto_se_bankid_samedevice:
            case l.BenyttetSigneringsmetode.criipto_se_bankid_anotherdevice:
            case l.BenyttetSigneringsmetode.criipto_se_bankid_anotherdevice_qr:
                return this.gettextCatalog.getString('Svensk BankID');
            default:
                console.warn('Ukjent BenyttetSigneringsmetode ' + signaturtype)
                return l.BenyttetSigneringsmetode[l.BenyttetSigneringsmetode.legacy_autentisert];
        }
    }

    signeringsmetodeModeller(): SigneringsmetodeModell[] {
        return [
            {
                metode: l.Signeringsmetode.lederplan_autentisert_tofakt_sms,
                navn: this.gettextCatalog.getString('Engangskode per SMS'),
                logo: null
            }, {
                metode: l.Signeringsmetode.lederplan_autentisert_tofakt_epost,
                navn: this.gettextCatalog.getString('Engangskode per SMS'),
                logo: null
            }, {
                metode: l.Signeringsmetode.norsk_bankid_netsentrisk_pdf,
                navn: this.gettextCatalog.getString('Norsk BankID'),
                logo: noBankidLogo.default
            }, {
                metode: l.Signeringsmetode.norsk_bankid_netsentrisk_tekst,
                navn: this.gettextCatalog.getString('Norsk BankID web'),
                logo: noBankidLogo.default
            },
            {
                metode: l.Signeringsmetode.svensk_bankid_samedevice,
                navn: this.gettextCatalog.getString('Svensk BankID, på denne enheten'),
                logo: seBankidLogo.default
            },
            {
                metode: l.Signeringsmetode.svensk_bankid_anotherdevice,
                navn: this.gettextCatalog.getString('Svensk BankID, med personnummer'),
                logo: seBankidLogo.default
            },
            {
                metode: l.Signeringsmetode.svensk_bankid_anotherdeviceqr,
                navn: this.gettextCatalog.getString('Svensk BankID, med QR-kode'),
                logo: seBankidLogo.default
            }
        ];
    }

    signeringsmetodeModellerFor(metoder: l.Signeringsmetode[]): SigneringsmetodeModell[] {
        return this.signeringsmetodeModeller().filter(modell => _.includes(metoder, modell.metode))
    }

    /** Angir hvilke l.Signeringsmetode som er knyttet til angitt l.AkseptertSignaturmetode.
     * Merk at denne strukturen også er representert i java i AkseptertSignaturmetode.java - og må holdes i synk med denne */
    signeringsmetodeModellerFraAkseptertSignaturmetode(metode: l.AkseptertSignaturmetode): SignaturmetodeModell {
        switch (metode) {
            case l.AkseptertSignaturmetode.avansertelektronisk:
                return {
                    metode: metode,
                    navn: this.gettextCatalog.getString('Avansert elektronisk'),
                    sortering: 0,
                    signeringsmetoder: this.signeringsmetodeModellerFor(this.konfigurasjon.tilgjengeligeSigneringsmetoderAvansertElektronisk)
                };

            case l.AkseptertSignaturmetode.enkelelektronisk:
                return {
                    metode: metode,
                    navn: this.gettextCatalog.getString('Enkel elektronisk'),
                    sortering: 1,
                    signeringsmetoder: this.signeringsmetodeModellerFor([l.Signeringsmetode.lederplan_autentisert_tofakt_sms])
                };

            default:
                console.error(`Ukjent AkseptertSignaturmetode ${metode}`)
                return {
                    metode: metode,
                    navn: 'ukjent',
                    sortering: 2,
                    signeringsmetoder: []
                };
        }
    }
}
